import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from './api.service';

export interface DeskChatPatient {
  id: number;
  full_name: string;
  patient_number: string;
  card_number: string;
}

export interface DeskChatThread {
  id: number;
  name: string;
  unread_count: number;
  last_message: { body: string; sender_name: string; created_at: string } | null;
}

export interface DeskChatLiveEvent {
  id: number;
  clinic_id: number;
  clinic_name?: string;
  sender_id: number;
  sender_name: string;
  body: string;
  mine: boolean;
}

export interface DeskChatMessage {
  id: number;
  clinic_id: number;
  clinic_name?: string;
  body: string;
  sender_id: number;
  sender_name: string;
  sender_role: string;
  mine: boolean;
  patient: {
    id: number;
    name: string;
    patient_number: string | null;
    card_number: string | null;
  } | null;
  created_at: string;
}

@Injectable({
  providedIn: 'root',
})
export class DeskChatService {
  private readonly apiUrl: string;
  private attachablePatient: DeskChatPatient | null = null;
  private readonly openPatientSubject = new BehaviorSubject<number | null>(null);
  readonly openPatient$ = this.openPatientSubject.asObservable();

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
  ) {
    this.apiUrl = this.apiService.getBaseUrl();
  }

  private headers() {
    const token = localStorage.getItem('jwtToken');
    return {
      headers: new HttpHeaders({
        Authorization: `Bearer ${token}`,
      }),
    };
  }

  getThreads() {
    return this.http.get(this.apiUrl + '/desk-chat/threads', this.headers());
  }

  getMessages(clinicId: number) {
    return this.http.get(
      this.apiUrl + '/desk-chat/threads/' + clinicId + '/messages',
      this.headers(),
    );
  }

  sendMessage(clinicId: number, body: string, patientId?: number | null) {
    const payload: any = { body };
    if (patientId) {
      payload.patient_id = patientId;
    }
    return this.http.post(
      this.apiUrl + '/desk-chat/threads/' + clinicId + '/messages',
      payload,
      this.headers(),
    );
  }

  watch(): Observable<DeskChatLiveEvent> {
    return new Observable((subscriber) => {
      const controller = new AbortController();
      let after = 0;
      let stopped = false;

      const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

      const run = async () => {
        while (!stopped) {
          try {
            const token = localStorage.getItem('jwtToken');
            const response = await fetch(`${this.apiUrl}/desk-chat/stream?after=${after}`, {
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'text/event-stream',
              },
              signal: controller.signal,
            });
            if (!response.ok || !response.body) {
              await sleep(2000);
              continue;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            while (!stopped) {
              const { value, done } = await reader.read();
              if (done) {
                break;
              }
              buffer += decoder.decode(value, { stream: true });
              const chunks = buffer.split('\n\n');
              buffer = chunks.pop() || '';
              for (const chunk of chunks) {
                const parsed = this.parseSse(chunk);
                if (!parsed) {
                  continue;
                }
                if (parsed.event === 'ready' || parsed.event === 'ping') {
                  after = Math.max(after, Number(parsed.data?.after || 0));
                }
                if (parsed.event === 'message' && parsed.data?.id) {
                  after = Math.max(after, Number(parsed.data.id));
                  subscriber.next(parsed.data as DeskChatLiveEvent);
                }
              }
            }
          } catch {
            if (stopped) {
              return;
            }
            await sleep(2000);
          }
        }
      };

      void run();
      return () => {
        stopped = true;
        controller.abort();
      };
    });
  }

  markRead(clinicId: number) {
    return this.http.post(
      this.apiUrl + '/desk-chat/threads/' + clinicId + '/read',
      {},
      this.headers(),
    );
  }

  setAttachablePatient(patient: DeskChatPatient | null): void {
    this.attachablePatient = patient?.id ? patient : null;
  }

  getAttachablePatient(): DeskChatPatient | null {
    return this.attachablePatient;
  }

  requestOpenPatient(patientId: number): void {
    const id = Number(patientId || 0);
    if (!id) {
      return;
    }
    this.openPatientSubject.next(id);
  }

  clearOpenRequest(): void {
    if (this.openPatientSubject.value != null) {
      this.openPatientSubject.next(null);
    }
  }

  private parseSse(chunk: string): { event: string; data: any } | null {
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of chunk.split('\n')) {
      if (line.startsWith('event:')) {
        event = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trim());
      }
    }
    if (!dataLines.length) {
      return null;
    }
    try {
      return { event, data: JSON.parse(dataLines.join('\n')) };
    } catch {
      return null;
    }
  }
}
